import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { conversationsApi } from "@/lib/api";
import { toast } from "sonner";
import { normalizeInboxQuickFilter } from "@/lib/inboxChannels";
import {
  applyOpenConversationUnreadClear,
  decrementInboxUnread,
  markConversationReadInList,
  resolveInboxSelectFirst,
  sameConversationId,
  stripInboxSelectParam,
  waitForDraftGeneration,
} from "@/lib/inboxHelpers";

function isRetryableDetailError(e, { silent, attempt }) {
  if (silent || attempt >= 1) return false;
  return e?.code === "network_error"
    || String(e?.message || "").toLowerCase().includes("failed to fetch");
}

function shouldClearDetailLoading(silent, clearLoading, requestId, currentRequestId) {
  return !silent && clearLoading && requestId === currentRequestId;
}

async function _processLoadDetailError({
  e,
  silent,
  attempt,
  requestId,
  detailRequestIdRef,
  pendingScrollTopRef,
  loadDetail,
  id,
  background,
  markReadOpt,
  setActiveConv,
  setActiveMessages,
  setDraftGenerating,
}) {
  if (requestId !== detailRequestIdRef.current) return { retry: false, clearLoading: true };

  if (isRetryableDetailError(e, { silent, attempt })) {
    await new Promise((resolve) => { setTimeout(resolve, 1500); });
    await loadDetail(id, { silent, background, attempt: attempt + 1, markRead: markReadOpt });
    return { retry: true, clearLoading: false };
  }

  pendingScrollTopRef.current = null;
  if (!silent) {
    toast.error(e?.message || "Could not load conversation");
    setActiveConv(null);
    setActiveMessages([]);
    setDraftGenerating(false);
  }
  return { retry: false, clearLoading: true };
}

function _monitorDraftGeneration({
  r,
  silent,
  id,
  draftWaitIdRef,
  draftWaitConvRef,
  setDraftGenerating,
  loadDetail,
}) {
  const genStatus = r.draft_generation?.status;
  // Show the loader whenever the backend reports a live compose job — including
  // silent polls (inbound auto-draft while the thread is already open). Previously
  // only non-silent loads started the waiter, so the UI stayed idle until refresh.
  if (genStatus === "generating") {
    setDraftGenerating(true);
    if (draftWaitConvRef.current === id) {
      return;
    }
    draftWaitConvRef.current = id;
    const waitId = ++draftWaitIdRef.current;
    waitForDraftGeneration(conversationsApi, id, r.draft_generation)
      .then(async (result) => {
        // Only cancel on a newer waiter / conversation change. Do NOT gate on
        // detailRequestId — silent polls bump that ref while we wait, which
        // previously skipped the refresh and cleared the loader before the
        // draft messages were hydrated (multi-second empty gap in the footer).
        if (waitId !== draftWaitIdRef.current) return;
        if (result?.status === "ready") {
          await loadDetail(id, { silent: true, background: true, markRead: false });
        }
      })
      .catch((e) => {
        if (waitId !== draftWaitIdRef.current) return;
        if (e?.code === "draft_generation_failed" || e?.code === "draft_generation_timeout") {
          toast.error(e.message || "Draft generation failed");
        }
      })
      .finally(() => {
        if (waitId === draftWaitIdRef.current) {
          draftWaitConvRef.current = null;
          setDraftGenerating(false);
        }
      });
    return;
  }

  // Only clear on an explicit (non-silent) detail load. Silent polls must not
  // clear a loader owned by Ask-to-draft / an in-flight waiter — that used to
  // drop draftGenerating mid-request and leave Ask+Send buttons spinning.
  if (!silent) {
    setDraftGenerating(false);
  }
}

export function useInboxData() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeChannel = searchParams.get("channel") || "all";
  const activeFilter = normalizeInboxQuickFilter(searchParams.get("filter"));
  const inboxQuery = searchParams.get("q") || "";
  const selectFirst = searchParams.get("select") === "first";

  const [search, setSearch] = useState(inboxQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [list, setList] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [inboxSummary, setInboxSummary] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(conversationId ?? null);
  const [activeConv, setActiveConv] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draftGenerating, setDraftGenerating] = useState(false);

  const threadScrollerRef = useRef(null);
  const pendingScrollTopRef = useRef(null);
  const prevFirstMessageIdRef = useRef(null);
  const detailRequestIdRef = useRef(0);
  const draftWaitIdRef = useRef(0);
  /** Conversation id currently watched by waitForDraftGeneration (dedupe silent polls). */
  const draftWaitConvRef = useRef(null);
  /** Last known message_count for the open thread — bump triggers silent detail refresh. */
  const openMessageCountRef = useRef(null);
  /** Conversation IDs successfully marked read in this session (lowercase). */
  const markedReadIdsRef = useRef(new Set());
  /** Subset of markedReadIdsRef where POST /read (or GET mark_read) confirmed. */
  const confirmedReadIdsRef = useRef(new Set());

  useEffect(() => {
    setSelectedId(conversationId ?? null);
  }, [conversationId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const current = next.get("q") || "";
    if (debouncedSearch === current) return;
    if (debouncedSearch) next.set("q", debouncedSearch);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, searchParams, setSearchParams]);

  const setActiveChannel = useCallback((channelId) => {
    const next = stripInboxSelectParam(searchParams);
    if (channelId === "all") next.delete("channel");
    else next.set("channel", channelId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setActiveFilter = useCallback((filterId) => {
    const next = stripInboxSelectParam(searchParams);
    if (filterId === "all") next.delete("filter");
    else next.set("filter", filterId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const navigateToConversation = useCallback((id) => {
    const qs = stripInboxSelectParam(searchParams).toString();
    navigate(qs ? `/inbox/${id}?${qs}` : `/inbox/${id}`);
  }, [navigate, searchParams]);

  const isSelectedConversation = useCallback((id) => (
    sameConversationId(selectedId, id) || sameConversationId(conversationId, id)
  ), [selectedId, conversationId]);

  const selectConversation = useCallback((id) => {
    setSelectedId(id);
    navigateToConversation(id);
  }, [navigateToConversation]);

  const acknowledgeRead = useCallback((id, channel) => {
    if (!id) return;
    markedReadIdsRef.current.add(String(id).toLowerCase());
    setList((prev) => {
      const result = markConversationReadInList(prev, id);
      if (result.openRow) {
        const ch = channel || result.openRow.channel;
        setInboxSummary((summary) => decrementInboxUnread(summary, ch));
      }
      return result.conversations;
    });
  }, []);

  const markConversationRead = useCallback(async (id, { background = false } = {}) => {
    if (!id) return false;
    try {
      await conversationsApi.markRead(id, { background });
      const key = String(id).toLowerCase();
      markedReadIdsRef.current.add(key);
      confirmedReadIdsRef.current.add(key);
      acknowledgeRead(id);
      return true;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("inbox mark-read failed", e?.message);
      }
      return false;
    }
  }, [acknowledgeRead]);

  const clearConversation = useCallback(() => {
    const leavingId = conversationId || selectedId;
    if (leavingId) {
      // Flush read state when leaving the thread without sending a reply.
      markConversationRead(leavingId, { background: true });
    }
    setSelectedId(null);
    setActiveConv(null);
    setActiveMessages([]);
    const qs = stripInboxSelectParam(searchParams).toString();
    navigate(qs ? `/inbox?${qs}` : "/inbox");
  }, [navigate, searchParams, conversationId, selectedId, markConversationRead]);

  const syncChannel = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await conversationsApi.syncInbox({
        channel: activeChannel !== "all" ? activeChannel : undefined,
      });
      if (r.inbox_summary) setInboxSummary(r.inbox_summary);
      return r;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("inbox sync failed", e?.message);
      }
      return null;
    } finally {
      setSyncing(false);
    }
  }, [activeChannel]);

  const refreshList = useCallback(async ({ sync = false, background = false } = {}) => {
    try {
      // Await Gmail/channel sync before listing so newly ingested mail appears
      // on this refresh instead of only on the next 15s poll.
      if (sync) {
        await syncChannel().catch(() => {});
      }
      const r = await conversationsApi.list({
        limit: 100,
        channel: activeChannel !== "all" ? activeChannel : undefined,
        q: inboxQuery || undefined,
        filter: activeFilter !== "all" ? activeFilter : undefined,
        background,
      });
      const incoming = r.conversations || [];
      let conversations = incoming;
      let summary = r.inbox_summary;
      if (conversationId) {
        const cleared = applyOpenConversationUnreadClear(
          incoming,
          summary,
          conversationId,
          { forceClear: true },
        );
        conversations = cleared.conversations;
        if (cleared.summary) summary = cleared.summary;
      } else if (markedReadIdsRef.current.size) {
        // Keep session-opened threads clear after leaving until mark-read is
        // confirmed. A later unread from the server after confirmation means a
        // new inbound — restore the red dot and drop the session mark.
        conversations = incoming.map((c) => {
          const key = String(c.id).toLowerCase();
          if (!markedReadIdsRef.current.has(key)) return c;
          if (!c.is_unread) {
            confirmedReadIdsRef.current.add(key);
            return c;
          }
          if (confirmedReadIdsRef.current.has(key)) {
            markedReadIdsRef.current.delete(key);
            confirmedReadIdsRef.current.delete(key);
            return c;
          }
          return { ...c, is_unread: false };
        });
      }
      setList(conversations);
      setListTotal(r.total ?? conversations.length);
      if (summary) setInboxSummary(summary);
      if (r.assistant_name) {
        const { usePersonaStore } = await import("@/store/usePersonaStore");
        usePersonaStore.getState().hydrateFromServer({ assistant_name: r.assistant_name });
      }
      return conversations;
    } catch (e) {
      toast.error(e?.message || "Could not load conversations");
      return [];
    } finally {
      setListLoading(false);
    }
  }, [activeChannel, activeFilter, inboxQuery, syncChannel, conversationId]);

  const loadDetail = useCallback(async (
    id,
    { silent = false, background = false, attempt = 0, markRead: markReadOpt } = {},
  ) => {
    if (!id) {
      setActiveConv(null);
      setActiveMessages([]);
      setDraftGenerating(false);
      draftWaitConvRef.current = null;
      return;
    }
    const requestId = ++detailRequestIdRef.current;
    
    if (silent && threadScrollerRef.current) {
      pendingScrollTopRef.current = threadScrollerRef.current.scrollTop;
    }
    const tabVisible = typeof document === "undefined" || document.visibilityState === "visible";
    const shouldMark = markReadOpt !== undefined ? Boolean(markReadOpt) : (!silent || tabVisible);

    // Optimistic unread clear only — GET marks read once. Do not await a separate POST.
    if (!silent) {
      setDetailLoading(true);
      acknowledgeRead(id);
    } else if (shouldMark) {
      acknowledgeRead(id);
    }

    let clearLoading = true;

    try {
      const r = await conversationsApi.get(id, {
        background,
        markRead: shouldMark,
      });
      if (requestId !== detailRequestIdRef.current) return;
      
      setActiveConv(r.conversation);
      setActiveMessages(r.messages || []);
      if (shouldMark) {
        acknowledgeRead(id, r.conversation?.channel);
      }

      _monitorDraftGeneration({
        r,
        silent,
        id,
        draftWaitIdRef,
        draftWaitConvRef,
        setDraftGenerating,
        loadDetail,
      });
    } catch (e) {
      const res = await _processLoadDetailError({
        e,
        silent,
        attempt,
        requestId,
        detailRequestIdRef,
        pendingScrollTopRef,
        loadDetail,
        id,
        background,
        markReadOpt,
        setActiveConv,
        setActiveMessages,
        setDraftGenerating,
      });
      clearLoading = res.clearLoading;
      if (res.retry) return;
    } finally {
      if (shouldClearDetailLoading(silent, clearLoading, requestId, detailRequestIdRef.current)) {
        setDetailLoading(false);
      }
    }
  }, [acknowledgeRead]);

  useEffect(() => {
    setListLoading(true);
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    const timer = setInterval(() => { refreshList({ background: true }); }, 15000);
    return () => clearInterval(timer);
  }, [refreshList]);

  useEffect(() => {
    if (conversationId) return;
    setActiveConv(null);
    setActiveMessages([]);
    setDraftGenerating(false);
  }, [conversationId]);

  useEffect(() => {
    draftWaitIdRef.current += 1;
    setDraftGenerating(false);
  }, [conversationId]);

  const hasActiveFilters = activeFilter !== "all" || !!inboxQuery;

  // Today Pulse deep-link only: `?select=first` opens the latest matching row once.
  // Normal inbox filters / sidebar / conversation clicks never set this flag.
  useEffect(() => {
    const decision = resolveInboxSelectFirst({
      selectFirst,
      conversationId,
      listLoading,
      list,
    });
    if (decision.action === "none" || decision.action === "wait") return;

    const next = stripInboxSelectParam(searchParams);
    const qs = next.toString();
    if (decision.action === "scrub") {
      setSearchParams(next, { replace: true });
      return;
    }
    navigate(qs ? `/inbox/${decision.id}?${qs}` : `/inbox/${decision.id}`, { replace: true });
  }, [
    selectFirst,
    conversationId,
    listLoading,
    list,
    searchParams,
    navigate,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!conversationId) return;
    if (activeChannel !== "all" && list.length === 0 && !hasActiveFilters) {
      setActiveConv(null);
      setActiveMessages([]);
      const qs = stripInboxSelectParam(searchParams).toString();
      navigate(qs ? `/inbox?${qs}` : "/inbox", { replace: true });
    }
  }, [conversationId, list, activeChannel, hasActiveFilters, navigate, searchParams]);

  useEffect(() => {
    if (!conversationId) return;
    loadDetail(conversationId);
  }, [conversationId, loadDetail]);

  useEffect(() => {
    openMessageCountRef.current = null;
    draftWaitConvRef.current = null;
  }, [conversationId]);

  // When the conversation list shows new messages on the open thread, refresh
  // detail immediately so inbound + auto-draft "generating" appear without a
  // full page reload (list polls every 15s; detail alone every 10s).
  useEffect(() => {
    if (!conversationId || !list.length) return;
    const row = list.find((c) => sameConversationId(c.id, conversationId));
    if (!row) return;
    const count = Number(row.message_count) || 0;
    const prev = openMessageCountRef.current;
    openMessageCountRef.current = count;
    if (prev != null && count > prev) {
      loadDetail(conversationId, { silent: true, background: true, markRead: false });
    }
  }, [list, conversationId, loadDetail]);

  useEffect(() => {
    if (!conversationId) return undefined;
    const timer = setInterval(() => {
      loadDetail(conversationId, { silent: true, background: true, markRead: false });
    }, 10000);
    return () => clearInterval(timer);
  }, [conversationId, loadDetail]);

  const maintainThreadScroll = useCallback((visibleMessages) => {
    const el = threadScrollerRef.current;
    if (!el) return;
    const firstId = visibleMessages[0]?.id ?? null;
    if (pendingScrollTopRef.current !== null) {
      el.scrollTop = pendingScrollTopRef.current;
      pendingScrollTopRef.current = null;
    } else if (
      firstId
      && prevFirstMessageIdRef.current
      && firstId !== prevFirstMessageIdRef.current
      && el.scrollTop < 80
    ) {
      el.scrollTop = 0;
    }
    prevFirstMessageIdRef.current = firstId;
  }, []);

  const globalEmpty = !listLoading
    && (inboxSummary?.total || 0) === 0
    && !(inboxSummary?.configured_channels?.length);

  return {
    conversationId,
    activeChannel,
    activeFilter,
    inboxQuery,
    hasActiveFilters,
    globalEmpty,
    search,
    setSearch,
    list,
    setList,
    listTotal,
    inboxSummary,
    listLoading,
    syncing,
    activeConv,
    setActiveConv,
    activeMessages,
    setActiveMessages,
    detailLoading,
    draftGenerating,
    setDraftGenerating,
    threadScrollerRef,
    setActiveChannel,
    setActiveFilter,
    isSelectedConversation,
    selectConversation,
    clearConversation,
    refreshList,
    loadDetail,
    maintainThreadScroll,
  };
}
